import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Table, Button, Popconfirm, Space, Input, Select, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, SearchOutlined, CopyOutlined, ClearOutlined } from '@ant-design/icons';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { columnLabels, STATUSES } from '../../constants/vacancyRequestColumnLabels';
import StatusTag from '../StatusTag';
import { canEditVacancyRequest, canRevokeVacancyRequest } from '../../utils/vacancyRequestPermissions';


const statusOptions = [
  { value: 'template', label: 'Черновик' },
  { value: 'pending', label: 'На согласовании' },
  { value: 'new', label: 'Новая' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'closed', label: 'Закрыта' },
  { value: 'rejected', label: 'Отклонена' },
  { value: 'withdrawn', label: 'Отозвана' },
  { value: 'archived', label: 'Архив' }
];

const ListView = ({
  requests = [],
  loading,
  onDelete,
  onEditRequest,
  onViewRequest,
  onCopyRequest,
  visibleColumns = [],
  organisations = [],
  users = [],
  pageSize = 10,
  showSizeChanger = true,
  showQuickJumper = true,
  // Пропсы для server-side пагинации
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  // Пропсы для фильтров
  filters: externalFilters,
  onFiltersChange,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isCandidatesPage = location.pathname.includes('/vacancy-requests');
  // Используем внешние фильтры, если они переданы, иначе локальное состояние
  const [localFilters, setLocalFilters] = useState({});
  const filters = externalFilters !== undefined ? externalFilters : localFilters;
  const setFilters = onFiltersChange || setLocalFilters;
  // Временные фильтры (еще не примененные)
  const [tempFilters, setTempFilters] = useState({});

  // Синхронизируем временные фильтры с примененными при изменении извне
  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);
  const { user, role } = useSelector((state) => state.auth);
  const userId = user?.id;
  const storedRole = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
  const effectiveRole = role || storedRole;

  const getNameById = (arr = [], id, field = 'name') => {
    const item = arr?.find?.(i => i.organisation_id === id);
    return item ? item[field] : 'Не указано';
  };

  const getUserName = (id) => {
    const user = users?.find?.(u => u.id === id);
    return user ? `${user.first_name} ${user.last_name}` : 'Не указано';
  };

  const getDepartmentNameById = (id) => {
    for (const org of organisations) {
      const department = org.departments?.find(dep => dep.id === id);
      if (department) return department.name;
    }
    return 'Не указано';
  };

  const handleTableChange = (pagination, filters, sorter) => {
    // Сохраняем фильтры во временное состояние, но не применяем их
    const newTempFilters = {};
    Object.keys(filters).forEach(key => {
      if (filters[key] && filters[key].length > 0) {
        newTempFilters[key] = key === 'status' ? filters[key] : filters[key][0];
      }
    });
    setTempFilters(newTempFilters);
  };

  // Применение фильтров при нажатии на кнопку "Найти"
  const handleApplyFilters = useCallback(() => {
    setFilters(tempFilters);
  }, [tempFilters, setFilters]);

  // Сброс фильтров
  const handleResetFilters = useCallback(() => {
    setTempFilters({});
    setFilters({});
  }, [setFilters]);

  const requiresCommentForEdit = (status, isTemplate) => {
    if (isTemplate) {
      return false;
    }
    return ['pending', 'new', 'in_progress'].includes(status);
  };

  const isEditDisabled = (record) => {
    if (!record) {
      return true;
    }
    if (['closed', 'archived', 'withdrawn'].includes(record.status)) {
      return true;
    }

    return !canEditVacancyRequest(effectiveRole, userId, {
      authorId: record.author,
      approverHrId: record.approver_hr_id
    });
  };

  const canDelete = (record) => {
    if (!record) {
      return false;
    }
    return record.is_template && canRevokeVacancyRequest(effectiveRole, userId, {
      authorId: record.author,
      approverHrId: record.approver_hr_id
    });
  };

  const hasTempFiltersForTitle = Object.keys(tempFilters).length > 0;

  const actionColumn = {
    title: () => (
      <Button
        type="primary"
        icon={<SearchOutlined />}
        onClick={handleApplyFilters}
        disabled={!hasTempFiltersForTitle}
        size="small"
      >
        Найти
      </Button>
    ),
    key: 'actions',
    width: 120,
    style: {
      wordBreak: 'keep-all',
      whiteSpace: 'nowrap'
    },
    onCell: () => ({
      style: {
        wordBreak: 'keep-all',
        whiteSpace: 'nowrap'
      },
      className: 'actions-column'
    }),
    render: (_, record) =>
      isCandidatesPage ? (
        <Space size="small">
          <Tooltip title="Просмотреть заявку">
            <Button icon={<EyeOutlined />} size="small" onClick={() => navigate(`/vacancy-requests/${record.id}`)} />
          </Tooltip>
          <Tooltip title={
            isEditDisabled(record)
              ? "Редактирование недоступно: недостаточно прав или заявка закрыта/архивирована/отозвана"
              : record.is_template
                ? "Редактировать шаблон"
                : requiresCommentForEdit(record.status, record.is_template)
                  ? "Редактировать заявку (требуется комментарий)"
                  : "Редактировать заявку"
          }>
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => !isEditDisabled(record) && onEditRequest(record)}
              type={requiresCommentForEdit(record.status, record.is_template) ? "primary" : "default"}
              disabled={isEditDisabled(record)}
            />
          </Tooltip>
          <Tooltip title="Копировать заявку">
            <Button icon={<CopyOutlined />} size="small" onClick={() => onCopyRequest(record)} />
          </Tooltip>
          {canDelete(record) ? (
            <Popconfirm
              title="Удалить заявку?"
              onConfirm={() => onDelete(record.id)}
              okText="Да"
              cancelText="Нет"
            >
              <Tooltip title="Удалить заявку">
                <Button
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                />
              </Tooltip>
            </Popconfirm>
          ) : (
            <Tooltip title="Удаление доступно только для черновиков">
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                disabled
              />
            </Tooltip>
          )}
        </Space>
      ) : (
        <Link to={`/vacancy-requests`}>
          Перейти
        </Link>
      )
  };

  const columnRenderMap = {
    author: getUserName,
    assignee_id: getUserName,
    organisation: (id) => getNameById(organisations, id),
    department: getDepartmentNameById,
    status: (status, record) => {
      if (record.is_template) {
        return <StatusTag status="template" statuses={STATUSES} />;
      }

      return <StatusTag status={status} statuses={STATUSES} />;
    }
  };

  const generateColumns = useMemo(() => {
    if (!visibleColumns) return [];

    const baseCols = visibleColumns
      .filter(key => key !== 'actions')
      .map((key) => ({
        title: columnLabels[key],
        dataIndex: key,
        key,
        render: columnRenderMap[key],
        filteredValue: tempFilters[key] ? (key === 'status' ? (Array.isArray(tempFilters[key]) ? tempFilters[key] : [tempFilters[key]]) : [tempFilters[key]]) : null,
        sorter: key === 'id' ? (a, b) => a.id - b.id : undefined,
        ellipsis: false,
        width: key === 'id' ? 80 : undefined,
        style: {
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          maxWidth: key === 'id' ? 80 : 200
        },
        onCell: () => ({
          style: {
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            maxWidth: key === 'id' ? 80 : 200
          }
        }),
        // Отключаем встроенную фильтрацию Ant Design - используем только server-side фильтрацию
        onFilter: false,
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
          // Обработчик для применения фильтра (сохраняет во временное состояние)
          const handleApply = () => {
            const newTempFilters = { ...tempFilters };
            if (selectedKeys && selectedKeys.length > 0) {
              if (key === 'status') {
                newTempFilters[key] = selectedKeys;
              } else {
                newTempFilters[key] = selectedKeys[0];
              }
            } else {
              // Удаляем фильтр, если он был очищен
              delete newTempFilters[key];
            }
            setTempFilters(newTempFilters);
            confirm(); // Закрываем dropdown
          };

          // Обработчик для сброса фильтра
          const handleClear = () => {
            const newTempFilters = { ...tempFilters };
            delete newTempFilters[key];
            setTempFilters(newTempFilters);
            clearFilters();
            // Если это был последний фильтр, автоматически сбрасываем все фильтры
            if (Object.keys(newTempFilters).length === 0) {
              handleResetFilters();
            }
            confirm(); // Закрываем dropdown
          };

          if (key === 'id') {
            return (
              <div style={{ padding: 8 }}>
                <Input
                  placeholder="Поиск по ID"
                  value={selectedKeys[0]}
                  onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                  style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleApply}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Применить
                  </Button>
                  <Button onClick={() => {
                    clearFilters()
                    confirm();
                  }} size="small" style={{ width: 90 }}>
                    Сброс
                  </Button>
                </Space>
              </div>
            );
          }
          if (key === 'status') {
            return (
              <div style={{ padding: 8 }}>
                <Select
                  placeholder="Выберите статусы"
                  value={selectedKeys}
                  onChange={(value) => setSelectedKeys(value || [])}
                  style={{ width: 188, marginBottom: 8, display: 'block' }}
                  allowClear
                  mode="multiple"
                  options={statusOptions}
                  onBlur={() => { }}
                  dropdownStyle={{ zIndex: 1000 }}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleApply}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Применить
                  </Button>
                  <Button onClick={handleClear} size="small" style={{ width: 90 }}>
                    Сброс
                  </Button>
                </Space>
              </div>
            );
          }

          if (key === 'organisation') {
            const orgOptions = organisations?.map(org => ({
              value: String(org.organisation_id),
              label: org.name
            })) || [];

            return (
              <div style={{ padding: 8 }}>
                <Select
                  placeholder="Выберите компанию"
                  value={selectedKeys[0]}
                  onChange={(value) => setSelectedKeys(value ? [value] : [])}
                  style={{ width: 188, marginBottom: 8, display: 'block' }}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={orgOptions}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleApply}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Применить
                  </Button>
                  <Button onClick={handleClear} size="small" style={{ width: 90 }}>
                    Сброс
                  </Button>
                </Space>
              </div>
            );
          }

          if (key === 'department') {
            const departmentOptions = [];
            organisations?.forEach(org => {
              if (org.departments) {
                org.departments.forEach(dept => {
                  departmentOptions.push({
                    value: String(dept.id),
                    label: `${dept.name} (${org.name})`
                  });
                });
              }
            });

            return (
              <div style={{ padding: 8 }}>
                <Select
                  placeholder="Выберите отдел"
                  value={selectedKeys[0]}
                  onChange={(value) => setSelectedKeys(value ? [value] : [])}
                  style={{ width: 188, marginBottom: 8, display: 'block' }}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={departmentOptions}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleApply}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Применить
                  </Button>
                  <Button onClick={handleClear} size="small" style={{ width: 90 }}>
                    Сброс
                  </Button>
                </Space>
              </div>
            );
          }

          return (
            <div style={{ padding: 8 }}>
              <Input
                placeholder={`Поиск в ${columnLabels[key]}`}
                value={selectedKeys[0]}
                onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                style={{ width: 188, marginBottom: 8, display: 'block' }}
              />
              <Space>
                <Button
                  type="primary"
                  onClick={handleApply}
                  size="small"
                  style={{ width: 90 }}
                >
                  Применить
                </Button>
                <Button onClick={handleClear} size="small" style={{ width: 90 }}>
                  Сброс
                </Button>
              </Space>
            </div>
          );
        },
        filterIcon: (filtered) => {
          // Показываем активный фильтр, если есть во временных фильтрах
          const hasTempFilter = tempFilters[key] !== undefined && tempFilters[key] !== null && tempFilters[key] !== '';
          return <SearchOutlined style={{ color: hasTempFilter ? '#1890ff' : undefined }} />;
        },
      }));

    const includeActions = visibleColumns.includes('actions');
    return includeActions ? [...baseCols, actionColumn] : baseCols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleColumns, users, organisations, tempFilters, effectiveRole, userId, isCandidatesPage, navigate, onEditRequest, onCopyRequest, onDelete, handleApplyFilters, hasTempFiltersForTitle]);

  const hasActiveFilters = Object.keys(filters).length > 0;

  return (
    <div style={{
      '--table-cell-word-break': 'break-word',
      '--table-cell-white-space': 'normal'
    }}>
      {hasActiveFilters && (
        <div style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: '8px 0'
        }}>
          <Button
            icon={<ClearOutlined />}
            onClick={handleResetFilters}
            size="middle"
          >
            Сбросить фильтры
          </Button>
        </div>
      )}
      <Table
        columns={generateColumns}
        dataSource={requests}
        rowKey="id"
        bordered
        size="middle"
        locale={{
          triggerDesc: 'Нажмите для сортировки по убыванию',
          triggerAsc: 'Нажмите для сортировки по возрастанию',
          cancelSort: 'Сбросить сортировку',
        }}
        pagination={currentPage && totalPages !== undefined && onPageChange ? {
          current: currentPage,
          pageSize: pageSize,
          total: totalItems !== undefined && totalItems !== null ? totalItems : (totalPages * pageSize),
          showTotal: (total, range) => {
            const actualTotal = totalItems !== undefined && totalItems !== null ? totalItems : total;
            const endRange = Math.min(range[1], actualTotal);
            return `${range[0]}-${endRange} из ${actualTotal} записей`;
          },
          showSizeChanger: showSizeChanger,
          showQuickJumper: false,
          showLessItems: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page, size) => {
            onPageChange(page, size);
          },
          onShowSizeChange: (current, size) => {
            onPageChange(1, size);
          },
        } : {
          pageSize: pageSize,
          showTotal: (total, range) => {
            const actualTotal = requests.length;
            const endRange = Math.min(range[1], actualTotal);
            return `${range[0]}-${endRange} из ${actualTotal} записей`;
          },
          showQuickJumper: false,
          defaultPageSize: pageSize
        }}
        loading={loading}
        scroll={{ x: 'max-content' }}
        onChange={handleTableChange}
        onRow={(record) => ({
          onDoubleClick: () => navigate(`/vacancy-requests/${record.id}`),
          style: { cursor: 'pointer' }
        })}
        className="word-wrap-table"
      />
      <style jsx>{`
        .word-wrap-table .ant-table-cell {
          word-break: break-word !important;
          white-space: normal !important;
          vertical-align: top !important;
        }
        .word-wrap-table .ant-table-cell.actions-column {
          word-break: keep-all !important;
          white-space: nowrap !important;
        }
      `}</style>
    </div>
  );
};

export default ListView;
